import React from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import AuthActions from "actions/auth-actions";
import { addOrderItem } from "actions/classic-order-actions";
import CEBagActions from "actions/ce-bag-actions";
import { bagTabs, switchBagTab, toggleBag } from "actions/bag-actions";

import AtomDropDownSelectOption from "components/source/atoms/atom-drop-down-select-option";
import AtomErrorNotification from "components/source/atoms/atom-error-notification";
import RtrButton from "components/design-system/rtr-button";

import { userDataPropType } from "components/propTypes";

import { GodmotherErrorMap, checkout, analytics, flagsAndExperimentNames } from "rtr-constants";
import { isAuthorized } from "../hoc/with-user-data";

import { HEAP_AUTH_TRIGGER_TYPES, HEAP_ADD_TO_BAG_IDS } from "helpers/heap-helpers";
import { addToBagCEEvent } from "helpers/heap-ce-helpers";
import GTMHelper from "helpers/gtm";
import { sendSailthruAddToCartEvent, configureSailthru } from "../../../helpers/third-party/sailthru-helpers";
import Converge from "helpers/converge";
import { closeGridProductDrawer } from "../../../slices/gridProductDrawer-slice";
import { isNotifyMeProduct } from "helpers/notify-me-helpers";
import UrgencySignalWrapper, {
  calculateEligiblityToShowUrgencySignalOnLegacyPdp,
} from "../products/urgency-signal/wrapper";
import { flagsAndExperimentsPropType } from "../hoc/with-flags-and-experiments";

// TODO:
// - what if no working filters?
export class KiffrAtbForm extends React.Component {
  static propTypes = {
    children: PropTypes.element,
    preSelectedSku: PropTypes.string,
    sizeNames: PropTypes.object.isRequired,
    product: PropTypes.object.isRequired,

    skus: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        size: PropTypes.string.isRequired,
        styleName: PropTypes.string.isRequired,
        unlimitedAvailabilities: PropTypes.arrayOf(
          PropTypes.shape({
            count: PropTypes.number,
          })
        ),
      })
    ).isRequired,
    userData: userDataPropType.isRequired,
    workingFilters: PropTypes.shape({
      zip_code: PropTypes.string,
    }),

    addToCEBag: PropTypes.func.isRequired,
    gridProductDrawer: PropTypes.object,
    getBag: PropTypes.func.isRequired,
    switchBagTab: PropTypes.func.isRequired,
    toggleBag: PropTypes.func.isRequired,
    closeGridProductDrawer: PropTypes.func.isRequired,
    showAuthModal: PropTypes.func.isRequired,
    sailthruConfig: PropTypes.object,
    notifyMeProducts: PropTypes.array,
    flagsAndExperiments: flagsAndExperimentsPropType,
  };

  state = { adding: false };

  skuIsAvailableForKif = sku => !!sku.unlimitedAvailabilities?.[0]?.count;

  handleAddToBag = () => {
    const { addToCEBag, userData, workingFilters, showAuthModal, product, notifyMeProducts, flagsAndExperiments } =
      this.props;

    if (!isAuthorized(userData)) {
      // callback prevents full page reload
      showAuthModal({ callback: () => {}, triggeredBy: HEAP_AUTH_TRIGGER_TYPES.AUTH_PROMPT });
      return;
    }

    this.clearBagErrors();

    const selectedSku = this.getSelectedSku();
    const loggingData = {
      object_type: "kif",
      pSize: selectedSku.size,
      styleName: selectedSku.styleName,
      membership: null,
      membership_type: null,
      membership_id: null,
      productId: product?.id,
      retailPriceInteger: product?.retailPriceInteger,
    };

    this.setState({ adding: true });

    addToCEBag(
      {
        sku: selectedSku.id,
        type: checkout.orderItemTypes.KIF_FROM_RACK, // "electiveSale"
        zipCode: workingFilters.zip_code,
        product: selectedSku,
      },
      false,
      loggingData,
      this.handleAddToBagSuccess,
      this.handleAddToBagError
    );

    const { isEligible, availabilityOfSelectedSku } = calculateEligiblityToShowUrgencySignalOnLegacyPdp(
      product?.skus,
      selectedSku.size,
      flagsAndExperiments?.[flagsAndExperimentNames.RSV_1421_URGENCY_SIGNAL],
      userData,
      false,
      true
    );

    addToBagCEEvent(analytics.RETAIL, analytics.KIFFR, {
      type: analytics.OBJECT_TYPE.BUY_PDP,
      is_notify_me: isNotifyMeProduct(product?.id, notifyMeProducts),
      urgencySignalShown: isEligible,
      urgencySignalItemsLeft: availabilityOfSelectedSku,
      urgencySignalExperimentStatus: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1421_URGENCY_SIGNAL],
      selectedSize: selectedSku.size,
    });
    this.setAddToBagAnalytics(selectedSku);
  };

  handleAddToBagSuccess = () => {
    const { switchBagTab, toggleBag, getBag, gridProductDrawer, closeGridProductDrawer } = this.props;
    this.setState({ adding: false });
    this.clearBagErrors();

    if (gridProductDrawer) {
      closeGridProductDrawer();
    }
    toggleBag(true);
    switchBagTab(bagTabs.CLASSIC_TAB);
    getBag();
  };

  setAddToBagAnalytics(selectedSku) {
    const { product, userData } = this.props;
    GTMHelper.addToCartEvent(product, selectedSku.id, false, userData?.userProfile?.id, userData?.userProfile?.email);
    Converge.trackAddedToCart(this.props.product, "kiffr");
    configureSailthru(this.props.sailthruConfig);
    sendSailthruAddToCartEvent(product, userData?.userProfile?.email, null, checkout.orderItemTypes.KIF_FROM_RACK);
  }

  handleAddToBagError = error => {
    const addToBagError = error || GodmotherErrorMap.defaultAddItemToOrder;
    this.setState({ addToBagError, adding: false });
  };

  handleSelectSize = size => {
    const { skus } = this.props;

    this.setState({ selectedSku: skus.find(sku => sku.size === size) });
  };

  clearBagErrors = () => this.setState({ addToBagError: null });

  getSelectedSku = () => {
    const { skus, preSelectedSku } = this.props;
    const { selectedSku } = this.state;

    if (selectedSku) {
      return selectedSku;
    }

    const hydratedPreselectedSku = preSelectedSku && skus.find(sku => sku.id === preSelectedSku);
    if (hydratedPreselectedSku) {
      return hydratedPreselectedSku;
    }

    if (skus.length === 1) {
      return skus[0];
    }

    return null;
  };

  getSizeName = size => {
    const { sizeNames } = this.props;

    return sizeNames[size] || size;
  };

  renderErrors() {
    const { addToBagError } = this.state;

    if (!addToBagError) return;

    return <AtomErrorNotification errorCopy={addToBagError} />;
  }

  renderSizeSelector() {
    const { sizeNames, skus } = this.props;

    if (skus.length > 1) {
      const dropdownValues = skus.reduce((acc, sku) => {
        const { size } = sku;
        const sizeName = sizeNames[size] || size;
        if (this.skuIsAvailableForKif(sku)) {
          return { ...acc, [size]: sizeName };
        } else {
          return { ...acc, [size]: `${sizeName} - Unavailable` };
        }
      }, {});

      const selectedSize = this.getSelectedSku()?.size;

      return (
        <AtomDropDownSelectOption
          dropdownValues={dropdownValues}
          onChange={this.handleSelectSize}
          placeholderOption="Size"
          selectorName="kif-size"
          selectedOption={selectedSize}
          optionalClassName="size-selector"
        />
      );
    }
  }

  renderAddToBagButton() {
    const { adding } = this.state;
    const selectedSku = this.getSelectedSku();

    let atbButtonText;
    let atbButtonEnabled;
    let atbButtonLoading = false;
    if (!selectedSku) {
      atbButtonText = "Select a size";
      atbButtonEnabled = false;
    } else if (!this.skuIsAvailableForKif(selectedSku)) {
      atbButtonText = "Currently unavailable";
      atbButtonEnabled = false;
    } else if (adding) {
      atbButtonText = "Adding";
      atbButtonEnabled = false;
      atbButtonLoading = true;
    } else {
      atbButtonText = "Add to bag";
      atbButtonEnabled = true;
    }

    return (
      <RtrButton
        fullWidth
        onClick={this.handleAddToBag}
        loading={atbButtonLoading}
        dataTestId="kiffr-atb-button"
        dataHeapId={HEAP_ADD_TO_BAG_IDS.KIFFR}
        disabled={!atbButtonEnabled}
        className="mt-6">
        {atbButtonText}
      </RtrButton>
    );
  }

  render() {
    const { children } = this.props;
    return (
      <>
        {this.renderErrors()}
        {this.renderSizeSelector()}
        <UrgencySignalWrapper
          className={`float-right ${this.props.skus?.length === 1 ? "-mt-[35px]" : "mr-10 -mt-8"}`}
          selectedSize={this.getSelectedSku()?.size}
        />
        {children}
        {this.renderAddToBagButton()}
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
    flagsAndExperiments: state.flagsAndExperiments,
    skus: state.product?.skus || [],
    userData: state.userData,
    workingFilters: state.workingFilters,
    gridProductDrawer: state.gridProductDrawer,
    sailthruConfig: {
      customerId: state.publicEnv?.sailthruCustomerId,
      url: state.initialUrl,
    },
    notifyMeProducts: state.notifyMeProducts,
  };
};

// NOTE: this is equivalent to the "object shorthand" explained in
// (https://react-redux.js.org/using-react-redux/connect-mapdispatch#defining-mapdispatchtoprops-as-an-object)
// It would also work to define mapDispatchToProps as simply { addOrderItem, switchBagTab, toggleBag }.
// However this would not allow us to spy on or mock the actions in tests, because their values would have already been
// passed into connect before the tests have a chance to manipulate them. By passing a function to connect, we delay the
// assignment of these props until the component is mounted, so tests are able to spy on them.
const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      addOrderItem,
      switchBagTab,
      toggleBag,
      closeGridProductDrawer,
      showAuthModal: AuthActions.showAuthModal,
      addToCEBag: CEBagActions.add,
      getBag: CEBagActions.get,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(KiffrAtbForm);
